// Shared primitives for every WMS screen — list search + pagination, date-range filter, the small
// field/badge helpers, and the lookup hooks. Extracted so the section pages (Inbound, Returns,
// Inventory, Warehouse, Reports …) can all use the same widgets without importing each other.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { api } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';
import { GmailPagination } from '@/components/ui/GmailPagination';

/** Rows per page for every WMS list screen. */
export const WMS_PAGE_SIZE = 25;

/**
 * The merchant portal renders WMS as a READ-ONLY, own-data view: only the data screens are shown and
 * every create/edit/delete/import control + the "Merchant" columns are hidden (the backend also scopes
 * reads to the merchant and blocks writes — see WmsMerchantGuard).
 */
export function useWmsViewOnly(): boolean {
  const { user } = useAuth();
  return user?.kind === 'merchant';
}

/**
 * Search + client-side pagination for WMS lists whose backend returns the full array.
 * `matches` decides whether a row matches the (already-lowercased/trimmed) search term.
 */
export function useSearchPaged<T>(rows: T[], matches: (row: T, term: string) => boolean, pageSize = WMS_PAGE_SIZE) {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const term = q.trim().toLowerCase();
  const filtered = term ? rows.filter((r) => matches(r, term)) : rows;
  const lastPage = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, lastPage);
  const offset = (safePage - 1) * pageSize;
  return {
    q,
    setQ: (v: string) => { setQ(v); setPage(1); },
    page: safePage,
    setPage,
    total: filtered.length,
    offset,
    pageRows: filtered.slice(offset, offset + pageSize),
  };
}

/** Consistent search box used across WMS lists. */
export function WmsSearch({ value, onChange, placeholder, className = 'w-64' }: { value: string; onChange: (v: string) => void; placeholder?: string; className?: string }) {
  return (
    <div className={`relative ${className}`}>
      <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder ?? 'Search…'}
        className="w-full border rounded pl-7 pr-2 py-1.5 text-sm" />
    </div>
  );
}

/** Right-aligned "1–25 of N" count + pager footer. */
export function WmsPageBar({ page, total, onPage, pageSize = WMS_PAGE_SIZE }: { page: number; total: number; onPage: (p: number) => void; pageSize?: number }) {
  return (
    <div className="flex justify-end items-center mt-3 min-h-[28px]">
      {total > 0 ? <GmailPagination page={page} pageSize={pageSize} total={total} onPage={onPage} /> : null}
    </div>
  );
}

/** Local-time YYYY-MM-DD for a date value (timezone-stable for range compares). */
export function wmsYmd(v: any): string | null {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** True if a date value falls within [from,to] (inclusive). Blank bounds are open-ended. */
export function wmsInDateRange(v: any, from: string, to: string): boolean {
  if (!from && !to) return true;
  const s = wmsYmd(v);
  if (!s) return false;
  if (from && s < from) return false;
  if (to && s > to) return false;
  return true;
}

/**
 * Human date + time in IST. The DB stores timestamps in UTC; slicing the raw ISO string would show
 * the UTC clock — ~5.5h behind India — as the "time".
 */
export function wmsDateTimeIST(v: any): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
}

/** From–To date filter used across WMS lists. */
export function WmsDateRange({ from, to, onFrom, onTo, label = 'Date' }: { from: string; to: string; onFrom: (v: string) => void; onTo: (v: string) => void; label?: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-slate-500 whitespace-nowrap">{label}</span>
      <input type="date" value={from} onChange={(e) => onFrom(e.target.value)} title={`${label} from`} className="border rounded px-2 py-1.5 text-sm" />
      <span className="text-slate-400 text-xs">–</span>
      <input type="date" value={to} onChange={(e) => onTo(e.target.value)} title={`${label} to`} className="border rounded px-2 py-1.5 text-sm" />
      {(from || to) && <button onClick={() => { onFrom(''); onTo(''); }} title="Clear dates" className="text-xs text-slate-400 hover:text-slate-700">✕</button>}
    </div>
  );
}

/** Labelled form field. */
export function F({ label, children }: any) {
  return <div><label className="block text-xs text-gray-500 mb-1">{label}</label>{children}</div>;
}

// Class strings must be written out in FULL — Tailwind scans source text, so a built-up
// `bg-${color}-100` is never emitted and the pill renders unstyled.
const BADGE_TONE = {
  emerald: 'bg-emerald-100 text-emerald-700',
  amber: 'bg-amber-100 text-amber-700',
  blue: 'bg-blue-100 text-blue-700',
  red: 'bg-red-100 text-red-700',
  slate: 'bg-slate-100 text-slate-700',
  purple: 'bg-purple-100 text-purple-700',
  orange: 'bg-orange-100 text-orange-700',
} as const;

const BADGE_COLORS: Record<string, keyof typeof BADGE_TONE> = {
  // inbound
  PENDING_PUTAWAY: 'amber', PENDING_QC: 'blue', COMPLETED: 'emerald',
  // returns
  RTO_INWARD: 'amber', QC: 'blue', RESTOCKED: 'emerald',
  // transfers / counts
  DRAFT: 'slate', COUNTED: 'blue', POSTED: 'emerald',
  // shared
  CANCELLED: 'red', RECEIVED: 'blue', PUTAWAY: 'amber', DISPATCHED: 'emerald', PICKING: 'blue',
  PASSED: 'emerald', REJECTED: 'red', PENDING: 'amber', PARTIAL: 'amber', DONE: 'emerald',
  GOOD: 'emerald', DAMAGED: 'red', DIFFERENT_PRODUCT: 'purple', MISSING_PARTS: 'orange',
  ACTIVE: 'emerald', EXPIRED: 'slate', TERMINATED: 'red',
};

/** Status pill. Unknown statuses fall back to slate rather than rendering unstyled. */
export function Badge({ text }: { text: string }) {
  const tone = BADGE_TONE[BADGE_COLORS[text] ?? 'slate'];
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap ${tone}`}>
      {String(text ?? '').replace(/_/g, ' ')}
    </span>
  );
}

/** Merchants with WMS enabled — every WMS merchant dropdown uses this (not the full merchant list). */
export function useWmsMerchants() {
  return useQuery({ queryKey: ['wms-merchants'], queryFn: async () => (await api.get('/wms/merchants')).data as any[] });
}

/** Active warehouses only. */
export function useActiveWarehouses() {
  return useQuery({ queryKey: ['wms-warehouses-active'], queryFn: async () => (await api.get('/wms/warehouses', { params: { status: 'ACTIVE' } })).data as any[] });
}

/** Internal users (for Requested/Approved/Dispatched By dropdowns). */
export function useUsersLight() {
  return useQuery({ queryKey: ['users-light'], queryFn: async () => (await api.get('/users', { params: { pageSize: 500 } })).data });
}

/** Full product catalogue, for SKU pickers. */
export function useAllProducts() {
  const { data } = useQuery({ queryKey: ['wms-products-all'], queryFn: async () => (await api.get('/wms/products', { params: { pageSize: 100000 } })).data as any });
  return (data?.items ?? []) as any[];
}

/** Options for SearchableSelect built from the product catalogue. */
export function productOptions(products: any[]) {
  return products.map((p) => ({
    value: p.id,
    label: `${p.sku} — ${p.name}`,
    hint: p.merchantId ? `M#${p.merchantId}` : '',
  }));
}

/** Panel wrapper every WMS screen sits in, with a title row and optional right-hand actions. */
export function WmsCard({ title, subtitle, actions, children }: { title?: React.ReactNode; subtitle?: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-white border rounded-xl p-4">
      {(title || actions) && (
        <div className="flex justify-between mb-3 gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            {title && <h3 className="font-semibold">{title}</h3>}
            {subtitle && <span className="text-xs text-slate-500">{subtitle}</span>}
          </div>
          {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

/** Placeholder for nav entries whose screen lands in a later phase. */
export function ComingSoon({ title, note }: { title: string; note?: string }) {
  return (
    <div className="bg-white border rounded-xl p-10 text-center">
      <div className="text-slate-800 font-semibold mb-1">{title}</div>
      <p className="text-sm text-slate-500 max-w-xl mx-auto">{note ?? 'This screen is planned for the next phase of the WMS build.'}</p>
    </div>
  );
}
